<template>
	<div>
		<div v-if="!isProductionEnv" class="top-banner text-uppercase">
			{{ currentEnv }}
		</div>
		<v-app
			:class="{
				'has-banner-padding': !isProductionEnv && !isPreviewActive,
				'has-banner-preview-padding': !isProductionEnv && isPreviewActive,
				'has-preview-padding': isProductionEnv && isPreviewActive,
			}"
		>
			<PreviewContainer :has-env-banner="!isProductionEnv" />
			<div v-if="fatal">
				<v-main>
					<v-container fluid>
						<v-alert type="error" prominent>{{ fatal }}</v-alert>
					</v-container>
				</v-main>
			</div>
			<div v-else-if="loading">
				<v-main>
					<v-container fluid>
						<v-alert type="info">{{ $t('aria.loading') }}..</v-alert>
					</v-container>
				</v-main>
			</div>
			<div v-else>
				<Navigation v-if="!isForbiddenPage" :mini="drawerMini" />
				<Notification v-if="!isForbiddenPage"></Notification>
				<restore-duty v-if="$route && $route.name === 'Planning' && isLoggedIn && showDeletedDuties" @close="showDeletedDuties = false"></restore-duty>
				<TopBar
					v-if="!isForbiddenPage"
					class="top-bar"
					:value="drawerMini"
					@input="(v) => (drawerMini = v)"
					@showDeletedDuties="showDeletedDuties = true"
				/>
				<v-container>
					<v-main :class="!isForbiddenPage ? 'page-wrapper' : ''">
						<router-view v-slot="{ Component }">
							<transition name="router-anim" enter-active-class="animated fadeIn">
								<component :is="Component" />
							</transition>
						</router-view>
					</v-main>
				</v-container>
			</div>
			<v-snackbar
				v-model="alertStore.isOpen"
				:timeout="alertStore.timeout"
				location="bottom"
				variant="flat"
				color="transparent"
				elevation="0"
				style="z-index: 9999"
			>
				<v-alert
					v-model="alertStore.isOpen"
					:type="alertStore.type"
					:text="alertStore.message"
					closable
					elevation="4"
					@click:close="alertStore.closeAlert"
				/>
			</v-snackbar>
		</v-app>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import Navigation from '@/views/Navigation';
import Notification from '@/views/Notification.vue';
import RestoreDuty from '@/views/RestoreDuty.vue';
import TopBar from '@/views/TopBar';
import i18n from '@/plugins/i18n';
import store from '@/store';
import { socketService } from '@/common/socket/SocketService';
import PlanningViewContainer from '@/rennovation/planning-view/containers/PlanningViewContainer.vue';
import { useAlertStore } from '/src/rennovation/planning-view/store/useAlertStore.js';
import PreviewContainer from '@/rennovation/planning-view/components/Layout/Modals/Preview/PreviewContainer.vue';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';

export default {
	name: 'App',

	components: {
		Navigation,
		Notification,
		RestoreDuty,
		TopBar,
		PlanningViewContainer,
		PreviewContainer,
	},
	setup() {
		const alertStore = useAlertStore();
		const { isActive: isPreviewActive, loadPreview, clearPreview } = useDutyChangePreview();

		return {
			alertStore,
			isPreviewActive,
			loadPreview,
			clearPreview,
		};
	},
	data: () => ({
		group: null,
		drawerMini: window.innerWidth < 1000,
		previousInnerWidth: window.innerWidth,
		showDeletedDuties: false,
		refreshing: false,
	}),
	computed: {
		...mapState({
			loading: (state) => state.lifecycle.loading,
			fatal: (state) => state.lifecycle.fatal,
			reload: (state) => state.setup.reload,
			user: (state) => state.auth.user,
		}),
		...mapGetters({
			isLoggedIn: 'auth/isLoggedIn',
			isSuperUser: 'auth/isSuperUser',
			showProductionBanner: 'profile/showProductionBanner',
		}),
		isProductionEnv() {
			return import.meta.env.VITE_STAGE === 'production';
		},
		currentEnv() {
			return import.meta.env.VITE_STAGE;
		},
		dynamicColor() {
			const envColors = {
				development: '#089406',
				alpha: '#FCBA03',
				production: '#ff6700',
				beta: '#6A1B9A',
			};
			return envColors[import.meta.env.VITE_STAGE] || '#044CB8';
		},
		isForbiddenPage() {
			return ['/forbidden'].includes(this.$route.path);
		},
	},
	methods: {
		...mapActions({
			getTenant: 'auth/getTenant',
			load: 'load',
		}),
		handleResize() {
			if (this.previousInnerWidth !== window.innerWidth) {
				this.previousInnerWidth = window.innerWidth;
				this.drawerMini = window.innerWidth < 1000;
			}
		},
		async reloadPage() {
			const registration = await navigator.serviceWorker.ready;

			if (!registration || !registration.waiting) {
				return;
			}
			registration.waiting.postMessage({
				type: 'SKIP_WAITING',
			});
		},
	},
	watch: {
		reload(newValue, oldValue) {
			if (newValue !== oldValue) {
				location.reload();
			}
		},
		isLoggedIn: {
			immediate: true,
			async handler(loggedIn) {
				if (loggedIn) {
					await this.loadPreview();
				} else {
					this.clearPreview();
				}
			},
		},
	},
	async beforeMount() {
		const tenant = await this.getTenant();
		this.load({ tenant, locale: navigator.language });
	},
	created() {
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('swUpdated', this.reloadPage, { once: true });

		if (navigator.serviceWorker) {
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				if (this.refreshing) return;
				this.refreshing = true;
				window.location.reload();
			});
		}

		store.dispatch('auth/validateToken');
	},

	unmounted() {
		window.removeEventListener('resize', this.handleResize);
	},
	updated() {
		const title = this.$route.meta?.title;
		const titleElement = document.querySelector('title');

		if (!titleElement || typeof title !== 'string' || !title) {
			return;
		}

		titleElement.textContent = i18n.t(title);
	},
	async beforeUnmount() {
		// Clean up the socket connection when the app is destroyed
		socketService.disconnect();
		await this.loadPreview();
	},
};
</script>

<style>
.top-banner {
	width: 100%;
	color: white;
	text-align: center;
	font-weight: 600;
	font-size: 12px;
	font-family: Arial, Helvetica, sans-serif;
	padding: 5px;
	position: fixed;
	top: 0;
	z-index: 3000;
	background-color: v-bind(dynamicColor);
}
.has-banner-padding {
	padding-top: 20px;
}
.has-banner-preview-padding {
	padding-top: 90px;
}
.has-preview-padding {
	padding-top: 70px;
}
.page-wrapper {
	padding-top: 30px;
	margin: 0px;
}
@media (min-width: 1280px) {
	.v-container {
		max-width: none;
	}
}
@media (min-width: 960px) {
	.v-container {
		max-width: none;
	}
}

.page-wrapper > .v-container {
	max-width: 100%;
}
</style>
